import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Avatar,
  useTheme,
  alpha,
  Container,
} from "@mui/material";
import {
  Info as InfoIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import adminModuleService from "../services/adminModuleService";
import Visual3DModuleBuilder from "./Visual3DModuleBuilder";

// Component đã được đơn giản hóa, loại bỏ 3D model preview

function ModuleEditor({
  open,
  onClose,
  onModuleUpdated,
  courses = [],
  moduleToEdit = null,
}) {
  const theme = useTheme();

  const [moduleForm, setModuleForm] = useState({
    name: "",
    description: "",
    courseId: "",
    version: 1,
    tags: [],
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modelFile, setModelFile] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const hasModel3D =
    moduleToEdit?.has3DModel ||
    moduleToEdit?.modelUrl ||
    moduleToEdit?.model3DUrl;

  // Load module data when moduleToEdit changes
  useEffect(() => {
    if (moduleToEdit && open) {
      setLoading(true);
      setModuleForm({
        name: moduleToEdit.name || "",
        description: moduleToEdit.description || "",
        courseId: moduleToEdit.course || "", // Use 'course' field from backend
        version: moduleToEdit.version || 1,
        tags: moduleToEdit.tags || [],
      });

      // Module data loaded

      setLoading(false);
    }
  }, [moduleToEdit, open]);

  // Handle tag addition
  const handleAddTag = () => {
    if (tagInput.trim() && !moduleForm.tags.includes(tagInput.trim())) {
      setModuleForm({
        ...moduleForm,
        tags: [...moduleForm.tags, tagInput.trim()],
      });
      setTagInput("");
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    setModuleForm({
      ...moduleForm,
      tags: moduleForm.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  // Handle form submission (simplified without 3D model)
  const handleSubmit = async () => {
    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      if (!moduleForm.name.trim()) {
        throw new Error("Module name is required");
      }
      if (!moduleForm.description.trim()) {
        throw new Error("Module description is required");
      }
      if (!moduleForm.courseId) {
        throw new Error("Course selection is required");
      }

      // Prepare update data
      const updateData = {
        name: moduleForm.name,
        description: moduleForm.description,
        course: moduleForm.courseId, // Course is now required
        tags: moduleForm.tags,
      };

      // Regular update without 3D model functionality
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 15, 90));
      }, 100);

      const result = await adminModuleService.updateModule(
        moduleToEdit.id,
        updateData
      );
      clearInterval(progressInterval);

      // If a 3D model file is selected, upload and replace the module's 3D model
      if (modelFile) {
        const formData = new FormData();
        formData.append("model", modelFile);
        formData.append("modelType", "auto");

        // Simulate upload progress while backend processes file
        const modelInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 95));
        }, 150);

        try {
          await adminModuleService.add3DModelToModule(
            moduleToEdit.id,
            formData
          );
          clearInterval(modelInterval);
          setUploadProgress(100);
        } catch (e) {
          clearInterval(modelInterval);
          throw e;
        }
      } else {
        setUploadProgress(100);
      }

      if (onModuleUpdated) {
        onModuleUpdated(result);
      }

      handleClose();
    } catch (error) {
      console.error("❌ Error updating module:", error);
      setError(error.message || "Failed to update module");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUpdateVersion = async () => {
    try {
      setUploading(true);
      setError(null);

      const result = await adminModuleService.updateModuleVersion(
        moduleToEdit.id
      );

      if (onModuleUpdated) {
        onModuleUpdated(result);
      }

      handleClose();
    } catch (error) {
      console.error("❌ Error updating module:", error);
      setError(error.message || "Failed to update module");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    setModuleForm({
      name: "",
      description: "",
      courseId: "",
      tags: [],
    });
    setError(null);
    setTagInput("");
    setModelFile(null);
    setViewerOpen(false);
    setUploading(false);
    setUploadProgress(0);

    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
            maxWidth: "1000px",
            margin: "0 auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "center", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            {" "}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "center", sm: "center" }}
              spacing={2}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 56,
                  height: 56,
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                <EditIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography
                  variant="h4"
                  component="div"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Edit Module
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Modify module details and 3D model
                </Typography>
              </Box>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                textAlign: { xs: "center", sm: "right" },
              }}
            >
              Version: {moduleForm.version}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "background.default" }}>
          <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
            <Stack spacing={{ xs: 2, sm: 3 }}>
              {/* Basic Information Section */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{
                    fontSize: { xs: "1.125rem", sm: "1.25rem" },
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <InfoIcon color="primary" />
                  Basic Information
                </Typography>

                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                  <TextField
                    label="Module Name"
                    value={moduleForm.name}
                    onChange={(e) =>
                      setModuleForm({ ...moduleForm, name: e.target.value })
                    }
                    fullWidth
                    disabled={uploading || loading}
                    variant="outlined"
                    required
                    error={!moduleForm.name && error}
                    helperText="Enter a descriptive name for your module"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <TextField
                    label="Description"
                    value={moduleForm.description}
                    onChange={(e) =>
                      setModuleForm({
                        ...moduleForm,
                        description: e.target.value,
                      })
                    }
                    multiline
                    rows={4}
                    fullWidth
                    disabled={uploading || loading}
                    variant="outlined"
                    required
                    error={!moduleForm.description && error}
                    helperText="Provide a detailed description of what users will learn"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        disabled={uploading || loading}
                        variant="outlined"
                        sx={{ width: "16rem" }}
                        required
                        error={!moduleForm.courseId && error}
                      >
                        <InputLabel>Course</InputLabel>
                        <Select
                          value={moduleForm.courseId}
                          onChange={(e) =>
                            setModuleForm({
                              ...moduleForm,
                              courseId: e.target.value,
                            })
                          }
                          label="Course"
                          sx={{ borderRadius: 2 }}
                          required
                        >
                          {courses.map((course) => (
                            <MenuItem key={course.id} value={course.id}>
                              {course.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {!moduleForm.courseId && error && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, ml: 1.75 }}
                          >
                            Course selection is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              {hasModel3D && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.125rem", sm: "1.25rem" },
                      mb: 2,
                    }}
                  >
                    3D Model (optional)
                  </Typography>

                  <Stack spacing={2}>
                    {moduleToEdit?.modelUrl && (
                      <Typography severity="info" sx={{ borderRadius: 2 }}>
                        Current model:{" "}
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setViewerOpen(true);
                          }}
                        >
                          View current model
                        </a>
                      </Typography>
                    )}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Button
                        variant="outlined"
                        component="label"
                        disabled={uploading || loading}
                        sx={{ borderRadius: 2 }}
                      >
                        Choose 3D file (.glb, .gltf)
                        <input
                          type="file"
                          hidden
                          accept=".glb,.gltf,.obj"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setModelFile(file);
                          }}
                        />
                      </Button>

                      {modelFile && (
                        <Chip
                          label={modelFile.name}
                          onDelete={() => setModelFile(null)}
                          sx={{ borderRadius: 2 }}
                        />
                      )}
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      Select a file to replace the module's 3D model. If no file
                      is selected, only basic details will be updated.
                    </Typography>
                  </Stack>
                </Paper>
              )}

              {/* Tags Section */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{
                    fontSize: { xs: "1.125rem", sm: "1.25rem" },
                    mb: 2,
                  }}
                >
                  Tags
                </Typography>

                <Stack spacing={2}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      label="Add Tag"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      size="small"
                      disabled={uploading || loading}
                      sx={{
                        flexGrow: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddTag}
                      disabled={!tagInput.trim() || uploading || loading}
                      sx={{ borderRadius: 2, minWidth: "auto", px: 3 }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>

                  {moduleForm.tags.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {moduleForm.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          onDelete={() => handleRemoveTag(tag)}
                          disabled={uploading || loading}
                          color="primary"
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      ))}
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Container>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 3 },
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            gap: { xs: 0.5, sm: 2 },
            flexWrap: "wrap",
          }}
        >
          {uploading && (
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ mb: 1, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Updating module... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ flexGrow: 1, borderRadius: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              onClick={handleClose}
              disabled={uploading}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: 2,
                flexBasis: { xs: '33.33%', sm: 'auto' },
                flexGrow: { xs: 1, sm: 0 },
                minWidth: { xs: 0, sm: 100 },
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 0.5, sm: 2 },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateVersion}
              disabled={uploading}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: 2,
                flexBasis: { xs: '33.33%', sm: 'auto' },
                flexGrow: { xs: 1, sm: 0 },
                minWidth: { xs: 0, sm: 100 },
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 0.5, sm: 2 },
              }}
            >
              {uploading ? "..." : "Update"}
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={
                uploading ||
                loading ||
                !moduleForm.name ||
                !moduleForm.description ||
                !moduleForm.courseId
              }
              variant="contained"
              size="small"
              sx={{
                borderRadius: 2,
                flexBasis: { xs: '33.33%', sm: 'auto' },
                flexGrow: { xs: 1, sm: 0 },
                minWidth: { xs: 0, sm: 100 },
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 0.5, sm: 2 },
                bgcolor: "primary.main",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              {uploading ? "..." : "Save"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      {hasModel3D && (
        <Dialog
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          maxWidth={false}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "95vw",
              height: "90vh",
              maxWidth: "none",
              maxHeight: "none",
              borderRadius: 1,
            },
          }}
        >
          <DialogContent sx={{ p: 0, height: "100%" }}>
            <Visual3DModuleBuilder
              moduleId={moduleToEdit?.id}
              moduleName={moduleToEdit?.name}
              onCancel={() => setViewerOpen(false)}
              onSave={() => setViewerOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default ModuleEditor;
